/**
 * Single source of truth for everything that crosses the editor / AI-workflow
 * boundary: the list of block types, the serialized HTML shape (tags +
 * data-attributes), the `blockToHtml` emitter, the LLM prompt blurbs, and the
 * text-bearing field node names. Framework-agnostic — no React, no Tiptap.
 *
 * Per-block Tiptap extensions, React node views, keyboard shortcuts, and
 * default-content JSON builders live in each block's folder and consume this
 * registry for HTML attribute names so they cannot drift.
 */

import type { SceneTemplateVariation } from "@/features/lomdaEditorV2/lib/lomdaTypes";

export const SCENE_BLOCK_TYPES = [
    "numberedSteps",
    "outlineCards",
    "imageGrid",
] as const;

export type SceneBlockType = (typeof SCENE_BLOCK_TYPES)[number];

export interface SceneBlockItemPayload {
    title: string;
    body: string;
    /** imageGrid only — populated by the AI image-gen pool, NOT by the LLM. */
    image?: string;
    /** imageGrid only — Imagen prompt the LLM writes per item. */
    visualConcept?: string;
}

export interface SceneBlockFieldDescriptor {
    nodeName: string;
    htmlTag: "h3" | "p";
}

export interface SceneBlockDescriptor {
    type: SceneBlockType;
    nodeName: string;
    itemNodeName: string;
    titleField: SceneBlockFieldDescriptor;
    bodyField: SceneBlockFieldDescriptor;
    blockTag: "ol" | "div";
    itemTag: "li" | "div";
    hasImage: boolean;
    itemCount: { default: number; min: number; max: number };
    /** Templates this block is allowed to appear in. Validator drops blocks placed on any other template. */
    supportedTemplates: readonly SceneTemplateVariation[];
    /**
     * Whether the 2B LLM may surround the block with prose:
     * - "forbidden": label + heading only.
     * - "encouraged": label + heading + ONE 1-2 sentence intro paragraph.
     */
    surroundingProse: "forbidden" | "encouraged";
    outlinePromptBlurb: string;
    contentPromptInstructions: string;
}

// `satisfies` (not an explicit annotation) preserves per-key literal types
// so Tiptap's `as`-prop narrowing works at call sites.
export const SCENE_BLOCK_REGISTRY = {
    numberedSteps: {
        type: "numberedSteps",
        nodeName: "numberedSteps",
        itemNodeName: "numberedStepsItem",
        titleField: { nodeName: "numberedStepsTitle", htmlTag: "h3" },
        bodyField: { nodeName: "numberedStepsBody", htmlTag: "p" },
        blockTag: "ol",
        itemTag: "li",
        hasImage: false,
        itemCount: { default: 3, min: 2, max: 5 },
        supportedTemplates: ["C"],
        surroundingProse: "forbidden",
        outlinePromptBlurb: `**\`"numberedSteps"\`** — A short ORDERED process. The reader follows step 1, then 2, then 3. Use for: "how to X", a methodology, phases of a process, instructions. Each item: a 1-6 word title + 5-20 word body. Item count: 2-5. Use ONLY in template C scenes.`,
        contentPromptInstructions: `## Custom block — numberedSteps (REQUIRED)
This scene uses a **numberedSteps** block. Populate \`block.items\` with 2-5 items (default 3) describing an ORDERED process — step 1, then 2, then 3.
- Each item.title: 1-6 words naming the step.
- Each item.body: 5-20 words explaining what happens in that step.
- Use this when order matters (a methodology, phases, instructions). The reader follows them in sequence.
- Do NOT include item numbers in the title — the block renders them automatically.
- Set block.type = "numberedSteps".
- IMPORTANT — surrounding prose: scene \`content\` must be ONLY the label + heading. Do NOT add a paragraph before or after the block; do NOT add bullet lists or any other prose. The numbered steps ARE the body of the scene.`,
    },
    outlineCards: {
        type: "outlineCards",
        nodeName: "outlineCards",
        itemNodeName: "outlineCardsItem",
        titleField: { nodeName: "outlineCardsTitle", htmlTag: "h3" },
        bodyField: { nodeName: "outlineCardsBody", htmlTag: "p" },
        blockTag: "div",
        itemTag: "div",
        hasImage: false,
        itemCount: { default: 3, min: 2, max: 4 },
        supportedTemplates: ["C"],
        surroundingProse: "encouraged",
        outlinePromptBlurb: `**\`"outlineCards"\`** — A small set of PARALLEL ideas at the same conceptual level. Use for: 3 benefits, 3 principles, 3 categories, 3 reasons, 3 comparison points. Each item: a 1-6 word title + 5-20 word body. Item count: 2-4. Use ONLY in template C scenes.`,
        contentPromptInstructions: `## Custom block — outlineCards (REQUIRED)
This scene uses an **outlineCards** block. Populate \`block.items\` with 2-4 items (default 3) representing PARALLEL ideas at the same level — peers, not sequential.
- Each item.title: 1-6 words naming the idea.
- Each item.body: 5-20 words explaining the idea.
- Use this for benefits, principles, categories, comparison points — items the reader scans rather than follows in order.
- Set block.type = "outlineCards".
- IMPORTANT — surrounding prose: outlineCards look better with a brief intro. Add ONE short paragraph (1-2 sentences, ~15-30 words) AFTER the heading and BEFORE the block, framing what the cards collectively show. Do NOT add prose after the block. Do NOT add bullet lists or multiple paragraphs.`,
    },
    imageGrid: {
        type: "imageGrid",
        nodeName: "imageGrid",
        itemNodeName: "imageGridItem",
        titleField: { nodeName: "imageGridTitle", htmlTag: "h3" },
        bodyField: { nodeName: "imageGridBody", htmlTag: "p" },
        blockTag: "div",
        itemTag: "div",
        hasImage: true,
        itemCount: { default: 3, min: 2, max: 4 },
        supportedTemplates: ["C"],
        surroundingProse: "forbidden",
        outlinePromptBlurb: `**\`"imageGrid"\`** — A small set of PARALLEL items where each item is best understood with its own visual. Use for: real-world examples, case studies, tangible objects/scenarios. Each item gets its own AI-generated image. Each item: a 1-6 word title + 5-20 word body. Item count: 2-4. Pick this only when visuals genuinely add value — for abstract ideas, prefer outlineCards. Use ONLY in template C scenes.`,
        contentPromptInstructions: `## Custom block — imageGrid (REQUIRED)
This scene uses an **imageGrid** block. Each item gets its own AI-generated tile image, so for every item you MUST also write a \`visualConcept\`.
Populate \`block.items\` with 2-4 items (default 3):
- Each item.title: 1-6 words.
- Each item.body: 5-20 words.
- Each item.visualConcept: ONE sentence (HARD LIMIT: 300 characters) describing a single concrete subject for the tile image. Examples: "A clean clinical workspace with a stethoscope on a table", "A young engineer reviewing code on a laptop". Avoid abstract concepts (those generate poor tile images), avoid composite scenes, avoid text/labels in the image. Write ONLY the visual description — no filler, repeated punctuation, padding, or meta-commentary about the prompt itself.
- Use this when concrete visuals reinforce parallel items (case studies, real-world examples, tangible objects). Do NOT use it for abstract ideas — pick outlineCards for those.
- Set block.type = "imageGrid".
- IMPORTANT — surrounding prose: scene \`content\` must be ONLY the label + heading. Do NOT add a paragraph before or after the block; do NOT add bullet lists or any other prose. The image grid IS the body of the scene.`,
    },
} satisfies Record<SceneBlockType, SceneBlockDescriptor>;

/** Text-bearing block-field node names — consumed by TextAlign / TextDirection. */
export const SCENE_BLOCK_FIELD_NODE_NAMES: string[] = SCENE_BLOCK_TYPES.flatMap(
    (t) => [
        SCENE_BLOCK_REGISTRY[t].titleField.nodeName,
        SCENE_BLOCK_REGISTRY[t].bodyField.nodeName,
    ]
);

const ESCAPE_RE = /[&<>"']/g;
const ESCAPE_MAP: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
};

function escapeHtml(str: string): string {
    return str.replace(ESCAPE_RE, (ch) => ESCAPE_MAP[ch]!);
}

/** Returns "" for empty `items` so callers can string-concatenate without guards. */
export function blockToHtml(
    type: SceneBlockType,
    items: SceneBlockItemPayload[]
): string {
    if (items.length === 0) return "";
    const d = SCENE_BLOCK_REGISTRY[type];

    const itemsHtml = items
        .map((item) => {
            const imageAttr =
                d.hasImage && item.image
                    ? ` data-image="${escapeHtml(item.image)}"`
                    : "";
            const title = escapeHtml(item.title.trim());
            const body = escapeHtml(item.body.trim());
            return (
                `<${d.itemTag} data-scene-block-item="${type}"${imageAttr}>` +
                `<${d.titleField.htmlTag} data-scene-block-field="${d.titleField.nodeName}">${title}</${d.titleField.htmlTag}>` +
                `<${d.bodyField.htmlTag} data-scene-block-field="${d.bodyField.nodeName}">${body}</${d.bodyField.htmlTag}>` +
                `</${d.itemTag}>`
            );
        })
        .join("");

    return `<${d.blockTag} data-scene-block="${type}">${itemsHtml}</${d.blockTag}>`;
}
